import addInfoBarMessage from '../actions/addInfoBarMessage';
import onSmimeOptionsChange, { onSmimeOptionsDirty } from '../actions/onSmimeOptionsChange';
import removeInfobarMessage from '../actions/removeInfoBarMessage';
import { findComposePlugins } from '../plugins/ComposePlugins';
import ComposeViewState from '../store/schema/ComposeViewState';
import createAttachments from '../utils/createAttachments';
import getAllAttachments from '../utils/getAllAttachments';
import { infoBarAddHandler, infoBarRemoveHandler } from '../utils/infoBarHandlers';
import isInlineAttachmentStateType from 'owa-attachment-full-data/lib/utils/isInlineAttachmentStateType';
import { default as deleteAttachmentFromStore } from 'owa-attachment-model-store/lib/actions/deleteAttachment';
import getAttachment from 'owa-attachment-model-store/lib/selectors/getAttachment';
import { AttachmentState, AttachmentType } from 'owa-attachment-well-data';
import removeAttachmentFromWell from 'owa-attachment-well-data/lib/actions/removeAttachmentFromWell';
import updateChangeKeyOnItemId from 'owa-attachment-well-data/lib/actions/updateChangeKeyOnItemId';
import deleteAttachment from 'owa-attachment-well-data/lib/services/deleteAttachment';
import clearAttachmentInfoBarMessages from 'owa-attachment-well-data/lib/utils/AttachmentQueueManager/clearAttachmentInfoBarMessages';
import { ClientItemId } from 'owa-client-ids';
import createBlob from 'owa-encoding-utils/lib/utils/base64/createBlob';
import encode from 'owa-encoding-utils/lib/utils/base64/encode';
import blobToFile from 'owa-encoding-utils/lib/utils/blobToFile';
import { onDeleteSmimeAttachment } from 'owa-mail-store-actions';
import { getStore } from 'owa-mail-store/lib/store/Store';
import DeleteAttachmentResponseMessage from 'owa-service/lib/contract/DeleteAttachmentResponseMessage';
import { lazySmimeAdapter } from 'owa-smime-adapter';
import SmimeInstallationStatus from 'owa-smime-adapter/lib/store/schema/SmimeInstallationStatus';
import smimeStore from 'owa-smime-adapter/lib/store/store';
import { isInitialized } from 'owa-smime-adapter/lib/utils/smimeUtils';
import SmimeViewState from 'owa-smime/lib/store/schema/SmimeViewState';
import getSmimeAttachmentType from 'owa-smime/lib/utils/getSmimeAttachmentType';
import { format } from 'owa-strings';
import strings from 'owa-strings/owa-mail-strings';
import { trace } from 'owa-trace';
import { orchestrator } from 'satcheljs';
import {
    lazyCreateSmimeAttachmentsViaSmimeQueueManager,
    lazyInitializeAttachmentStateForSmimeAttachments,
} from 'owa-smime';
import {
    createLocalComputerFiles,
    createMailItemFileForSmime,
    AttachmentFileList,
} from 'owa-attachment-file-types';

import {
    ClientAttachmentId,
    SmimeAttachmentType,
    isSmimeAttachment,
    getTypeOfAttachment,
    TypeOfAttachment,
} from 'owa-attachment-model-store';

export const SMIME_CONTROL_ERROR_MESSAGE_ID = 'smimeAdapterInitializationError';
const MAIL_FILE_EXTENSION = '.eml';

/**
 * Orchestrator that fetches the S/MIME adapter if S/MIME options are set
 */
export default orchestrator(onSmimeOptionsChange, async actionMessage => {
    const { viewState, shouldEncryptMessageAsSmime, shouldSignMessageAsSmime } = actionMessage;
    const { smimeViewState } = viewState;
    // Do not perform any action if the viewState is same
    if (
        !isSmimeViewStateDirty(
            smimeViewState,
            shouldEncryptMessageAsSmime,
            shouldSignMessageAsSmime
        )
    ) {
        return;
    }
    clearAttachmentInfoBarMessages(infoBarRemoveHandler(viewState));
    const isSmimeModeDisabled = !shouldEncryptMessageAsSmime && !shouldSignMessageAsSmime;
    const isSmimeModeEnabled =
        !smimeViewState.shouldEncryptMessageAsSmime && !smimeViewState.shouldSignMessageAsSmime;

    onSmimeOptionsDirty(viewState, shouldEncryptMessageAsSmime, shouldSignMessageAsSmime);

    // S/MIME to non-S/MIME scenario
    isSmimeModeDisabled && onSmimeModeDisabled(viewState);

    // Non-S/MIME to S/MIME scenario
    isSmimeModeEnabled && onSmimeModeEnabled(viewState);

    const isSmimeMessage = shouldEncryptMessageAsSmime || shouldSignMessageAsSmime;
    isSmimeMessage && (await tryInitializeSmimeControl(viewState));
});

async function tryInitializeSmimeControl(viewState: ComposeViewState) {
    // Initialize the control, if not already initialized.
    if (!isInitialized(smimeStore.installationStatus)) {
        try {
            await lazySmimeAdapter.import();
        } catch (e) {
            trace.info('S/MIME: Unable to initialize the control');
        }
    }

    // Display infobar if control installation status is any value other than installed.
    if (smimeStore.installationStatus != SmimeInstallationStatus.Installed) {
        addInfoBarMessage(viewState, SMIME_CONTROL_ERROR_MESSAGE_ID);
    }
}

/**
 * This util will return
 * True - if the smimeViewState is dirty, False - otherwise
 */
function isSmimeViewStateDirty(
    smimeViewState: SmimeViewState,
    isMessageEncrypted: boolean,
    isMessageSigned: boolean
): boolean {
    if (smimeViewState) {
        const { shouldEncryptMessageAsSmime, shouldSignMessageAsSmime } = smimeViewState;
        return (
            shouldEncryptMessageAsSmime != isMessageEncrypted ||
            shouldSignMessageAsSmime != isMessageSigned
        );
    }
    return false;
}

async function onSmimeModeEnabled(viewState: ComposeViewState) {
    const attachments: AttachmentState[] = getAllAttachments(viewState);

    // Dont create queue if attachments are not present
    if (!attachments.length) {
        return;
    }
    try {
        const initializeAttachmentStateForSmimeAttachments = await lazyInitializeAttachmentStateForSmimeAttachments.import();
        initializeAttachmentStateForSmimeAttachments(viewState.attachmentWell, attachments);

        const createSmimeAttachmentsViaSmimeQueueManager = await lazyCreateSmimeAttachmentsViaSmimeQueueManager.import();
        createSmimeAttachmentsViaSmimeQueueManager(
            viewState.attachmentWell,
            viewState.itemId as ClientItemId,
            infoBarAddHandler(viewState) /* infoBarAddHandler */,
            infoBarRemoveHandler(viewState) /* infoBarRemoveHandler */,
            attachments,
            (attachment: AttachmentType) =>
                tryDeleteAttachmentsOnDownloadSuccess(attachment, viewState)
        );
    } catch (error) {
        trace.error('S/MIME action onSmimeModeEnabled failed' + error.message);
    }
}

async function onSmimeModeDisabled(viewState: ComposeViewState) {
    // Remove if any control error infobars are present.
    removeInfobarMessage(viewState, SMIME_CONTROL_ERROR_MESSAGE_ID);

    if (viewState.itemId) {
        // Check and remove p7m file if its a switch from S/MIME message to non-S/MIME message
        // and item is already created. We await this to ensure that the p7m attachment is deleted before the action is completed
        await tryDeleteSmimeAttachmentType(viewState);
    }

    moveAttachmentsFromClientToServer(viewState);
}

/**
 * For every S/MIME attachment, we should remove it from the well and create it again
 * using the upload queue once S/MIME is disabled
 * @param viewState
 */
function moveAttachmentsFromClientToServer(viewState: ComposeViewState): void {
    const attachments: AttachmentState[] = [
        ...viewState.attachmentWell.docViewAttachments,
        ...viewState.attachmentWell.inlineAttachments,
    ];
    attachments.forEach(async attachment => {
        const attachmentId = attachment.attachmentId.Id;
        const attachmentModel = getAttachment(attachment.attachmentId);
        if (attachmentModel && isSmimeAttachment(attachmentId)) {
            const smimeAttachment = attachmentModel.model as SmimeAttachmentType;
            if (!smimeAttachment) {
                return;
            }
            const attachmentType = getTypeOfAttachment(smimeAttachment);
            let changedFiles: AttachmentFileList;
            if (attachmentType === TypeOfAttachment.Mail) {
                changedFiles = createMailItemFileForSmime(
                    smimeAttachment.Name,
                    encode(smimeAttachment.MimeContent)
                );
            } else {
                const file = createFileFromSmimeAttachment(smimeAttachment);
                changedFiles = createLocalComputerFiles([file]);
            }
            // Remove existing attachments from the well
            removeAttachmentFromWell(viewState.attachmentWell, attachment);
            // Recreate the same attachments
            if (isInlineAttachmentStateType(attachment)) {
                createAttachments(
                    changedFiles,
                    viewState,
                    {
                        isInline: true,
                        isHiddenInline: true,
                        shouldShare: false,
                    },
                    null /* processInlineImageBeforeUpload */,
                    null /* infoBarAddHandler */,
                    null /* infoBarRemoveHandler */,
                    null /* onFinish */,
                    (
                        parentItemId: ClientItemId,
                        attachmentId: ClientAttachmentId,
                        attachmentType: AttachmentType
                    ) =>
                        onAttachmentCreated(
                            attachmentId,
                            attachmentType,
                            smimeAttachment.ContentId,
                            viewState
                        )
                );
            } else {
                createAttachments(
                    changedFiles,
                    viewState,
                    {
                        isInline: false,
                        isHiddenInline: true,
                        shouldShare: false,
                    },
                    null /* processInlineImageBeforeUpload */,
                    null /* infoBarAddHandler */,
                    null /* infoBarRemoveHandler */
                );
            }
        }
    });
}

function onAttachmentCreated(
    attachmentIdFromServer: ClientAttachmentId,
    attachmentType: AttachmentType,
    oldContentId: string,
    viewState: ComposeViewState
) {
    const plugins = findComposePlugins(viewState);
    plugins &&
        plugins.inlineImagePlugin.onSmimeUploadSuccess(
            oldContentId,
            attachmentIdFromServer,
            attachmentType.ContentId
        );
}

function createFileFromSmimeAttachment(smimeAttachment: SmimeAttachmentType) {
    const { Name, ContentType, MimeContent, Content } = smimeAttachment;
    const nameWithExtension = MimeContent ? Name + MAIL_FILE_EXTENSION : Name;
    const fileContent = MimeContent ? encode(MimeContent) : Content;
    const blob = createBlob(fileContent, ContentType);
    return blobToFile(blob, nameWithExtension);
}

// Delete the S/MIME attachment type (p7m file), if present in the item
async function tryDeleteSmimeAttachmentType(viewState: ComposeViewState) {
    const { itemId } = viewState;
    const item = getStore().items.get(itemId.Id);
    const smimeAttachment: AttachmentType = getSmimeAttachmentType(item);

    if (item && smimeAttachment && smimeAttachment.AttachmentId) {
        try {
            const attachmentId: ClientAttachmentId = {
                Id: smimeAttachment.AttachmentId.Id,
                mailboxInfo: item.MailboxInfo,
            };
            const response = await deleteAttachment(attachmentId);
            const responseMessage: DeleteAttachmentResponseMessage =
                response.ResponseMessages.Items[0];
            if (responseMessage.ResponseClass === 'Success') {
                updateChangeKeyOnItemId(itemId, responseMessage.RootItemId.RootItemChangeKey);
                // Update item in the store
                onDeleteSmimeAttachment(itemId.Id);
            } else {
                trace.warn(
                    `Smime Attachment: Failed to delete attachment with ResponseClass= ${
                        responseMessage.ResponseClass
                    }, ResponseCode=${responseMessage.ResponseCode}`
                );
            }
        } catch (error) {
            trace.warn('S/MIME: An error occurred while deleting the p7m file' + error.message);
            infoBarAddHandler(viewState)('WarningAttachmentCanNotBeDeleted', [
                format(strings.WarningAttachmentsCanNotBeDeleted, 'smime.p7m'),
            ]);
        }
    }
}

async function tryDeleteAttachmentsOnDownloadSuccess(
    attachment: AttachmentType,
    viewState: ComposeViewState
): Promise<void> {
    const attachmentToDelete = attachment.AttachmentId as ClientAttachmentId;
    // Remove from store
    deleteAttachmentFromStore(attachmentToDelete);

    // Delete from server
    try {
        const response = await deleteAttachment(attachmentToDelete);
        const responseMessage: DeleteAttachmentResponseMessage = response.ResponseMessages.Items[0];
        if (responseMessage.ResponseClass === 'Success') {
            trace.info('Attachment deleted successfully on toggle of S/MIME mode');
        } else {
            throw new Error(
                `Delete Smime Attachment toggle: Failed to delete attachment with ResponseClass= ${
                    responseMessage.ResponseClass
                }, ResponseCode=${responseMessage.ResponseCode}`
            );
        }
    } catch (error) {
        trace.warn(error.message);
        infoBarAddHandler(viewState)('WarningAttachmentCanNotBeDeleted', [
            format(strings.WarningAttachmentsCanNotBeDeleted, attachment.Name),
        ]);
    }
}
